import { useAuth0 } from '@auth0/auth0-react';
import { getModularBenefitsList } from '@ben/columbia/api';
import BenefitsTable from '@ben/columbia/components/benefits-table';
import Button from '@ben/orion/deprecated-button';
import { IconName } from '@ben/orion/icon';
import Search from '@ben/orion/search/search';
import { getStoredPageSize } from '@ben/orion/table';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useQuery } from '@tanstack/react-query';
import type { ColumnSort } from '@tanstack/react-table';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { usePrefetch } from './use-pre-fetch';

function BenefitListScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();

  const params = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const query = useMemo(() => {
    return {
      search: params.get('search') ?? undefined,
      page: params.get('page') ?? '1',
      pageSize: params.get('pageSize') ?? getStoredPageSize(),
      sort: params.get('sort') ?? '-id',
    };
  }, [params]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['columbiaBenefitList', query],
    queryFn: () =>
      getModularBenefitsList(
        Number(query.page),
        Number(query.pageSize),
        query.sort,
        query.search
      ),
    keepPreviousData: true,
    enabled: isAuthenticated,
  });

  usePrefetch(data);

  const onClickAddBenefit = useCallback(() => {
    navigate('/benefits/create');
  }, [navigate]);

  const onChangeSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        params.set('search', event.target.value);
      } else {
        params.delete('search');
      }
      params.delete('page'); // Reset page number when changing search term
      navigate({ search: params.toString() });
    },
    [navigate, params]
  );

  const onChangePage = useCallback(
    (pageIndex: number) => {
      params.set('page', String(pageIndex + 1));
      navigate({ search: params.toString() });
    },
    [navigate, params]
  );

  const onChangePageSize = useCallback(
    (pageSize: number) => {
      params.set('pageSize', String(pageSize));
      navigate({ search: params.toString() });
    },
    [navigate, params]
  );

  const onChangeSort = useCallback(
    (sortState: ColumnSort) => {
      if (sortState) {
        params.set('sort', `${sortState.desc ? '-' : ''}${sortState.id}`);
      } else {
        params.delete('search');
      }
      params.delete('page'); // Reset page number when changing sort
      navigate({ search: params.toString() });
    },
    [navigate, params]
  );

  return (
    <Wrapper>
      <Header>
        <Typography variant={TypographyVariant.h1}>Modular Benefits</Typography>
        <ToolsWrapper>
          <Search
            defaultValue={query.search}
            onChange={onChangeSearch}
            placeholder="Search benefit or employer name"
          />
          <Button
            label="Add benefit"
            iconName={IconName.Add}
            onClick={onClickAddBenefit}
          />
        </ToolsWrapper>
      </Header>
      <BenefitsTable
        benefits={data?.results}
        pageIndex={Number(query.page) - 1}
        pageSize={Number(query.pageSize)}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
        onChangeSort={onChangeSort}
        rowCount={data?.count}
        searchQuery={query.search}
        isLoading={isLoading || isFetching}
      />
    </Wrapper>
  );
}

export default BenefitListScreen;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(5)};
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const ToolsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing(3)};
  `}
`;
